// Archived Projects — read-only list of Closed/Archived/Delivered programs.
// Reads /api/planning-command-center/archived.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Loader2, Search } from "lucide-react";
import { fetchJson, fmtMoney, fmtDate, SectionHeader } from "./pccHelpers";

export default function PccArchived() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await fetchJson("/planning-command-center/archived");
        if (alive) setPrograms(d.programs || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
  if (error) return <Card className="bg-red-500/[0.06] border-red-500/30 p-4 text-red-200">{error}</Card>;

  const filtered = programs.filter(p =>
    !search ||
    (p.program_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.client_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="pcc-archived">
      <div>
        <h2 className="text-xl font-light text-white">Archived Projects</h2>
        <p className="text-white/55 text-sm">Closed, Archived, and Delivered programs.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search archived projects…" className="pl-9 bg-black/30 border-white/10 text-white" />
      </div>

      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <SectionHeader dot="#8A8783" title={`Archived (${filtered.length})`} />
        <div className="divide-y divide-white/[0.06] max-h-[700px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">No archived projects match.</div>
          ) : filtered.map(p => (
            <Link to={`/planning-command-center/projects/${p.id}`} key={p.id} className="px-6 py-3 flex items-center gap-3 hover:bg-white/[0.02]">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{p.program_name}</div>
                <div className="text-[11px] text-white/45 font-mono truncate">{p.client_name} · Closed {fmtDate(p.updated_at)}</div>
              </div>
              <Badge className="bg-white/[0.06] text-white/55 border-white/15">{p.stage}</Badge>
              <div className="text-sm font-mono tabular-nums w-28 text-right">{fmtMoney(p.contract_value)}</div>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
